/**
 * F606AuditLog — lightweight viewer for admin_actions_log.
 * Uses the generic list endpoint (bridge exposes admin logs at the /admin
 * scope — falls back to `/commands` if not).
 */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { RefreshCw, Loader2 } from 'lucide-react';
import { bridge } from './bridgeClient';

const KIND_META = {
  order_confirm: { color: 'text-cyan-300' },
  order_ship:    { color: 'text-blue-300' },
  order_deliver: { color: 'text-emerald-300' },
  order_cancel:  { color: 'text-red-300' },
  user_ban:      { color: 'text-red-300' },
  user_unban:    { color: 'text-emerald-300' },
  user_notify:   { color: 'text-cyan-300' },
  user_gift:     { color: 'text-purple-300' },
  sub_extend:    { color: 'text-cyan-300' },
  sub_cancel:    { color: 'text-red-300' },
  trainer_verify:{ color: 'text-emerald-300' },
  product_hide:  { color: 'text-amber-300' },
  broadcast:     { color: 'text-cyan-300' },
};

export default function F606AuditLog({ language }) {
  const isAr = language === 'ar';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    // The bridge exposes commands (queued/finished jobs).  Also try the
    // admin-actions-log entity if available.  If neither is reachable,
    // gracefully fall back to an empty state.
    const [cmds, actions] = await Promise.all([
      bridge.commands.list().catch(() => ({ data: [] })),
      bridge.list('admin-actions-log', { limit: 100 }).catch(() => ({ data: [] })),
    ]);
    const merged = [
      ...(actions.data || []).map((a) => ({ ...a, source: 'action' })),
      ...(cmds.data || []).map((c) => ({ ...c, source: 'command' })),
    ].sort((a, b) => String(b.at || b.created_at || '').localeCompare(String(a.at || a.created_at || '')));
    setRows(merged);
    if (!actions.success && !cmds.success && merged.length === 0) {
      toast.info(isAr ? 'لا يوجد سجلّ متاح حالياً' : 'No audit log endpoints reachable', { duration: 3000 });
    }
    setLoading(false);
  }, [isAr]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="f606-audit-log">
      <div className="flex justify-between items-center">
        <div className="text-white/60 text-xs">{rows.length} {isAr ? 'حدث' : 'events'}</div>
        <button onClick={load} className="p-2 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-white/10">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="rounded-2xl border border-white/10 bg-black/20 overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-white/5 text-white/50 text-[10px] uppercase tracking-wider">
              <th className="px-3 py-2 text-start">{isAr ? 'الحدث' : 'Action'}</th>
              <th className="px-3 py-2 text-start">{isAr ? 'الهدف' : 'Target'}</th>
              <th className="px-3 py-2 text-start">{isAr ? 'المنفّذ' : 'Actor'}</th>
              <th className="px-3 py-2 text-end">{isAr ? 'التوقيت' : 'When'}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading && rows.length === 0 && (
              <tr><td colSpan="4" className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin inline text-emerald-400/50" /></td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan="4" className="text-center py-8 text-white/40">
                {isAr ? 'لا يوجد سجلّ متاح — قد يكون endpoint غير مفعّل على الجسر بعد' : 'No audit log reachable — endpoint may not be enabled yet'}
              </td></tr>
            )}
            {rows.map((r, i) => {
              const kind = r.action || r.kind || 'unknown';
              const meta = KIND_META[kind] || { color: 'text-white/60' };
              return (
                <tr key={r.id || i} className="hover:bg-white/[0.02]" data-testid={`f606-audit-row-${i}`}>
                  <td className={`px-3 py-2 font-mono text-[11px] ${meta.color}`}>{kind}</td>
                  <td className="px-3 py-2 text-white/70">{r.target_summary || r.target_id || r.payload?.title || '—'}</td>
                  <td className="px-3 py-2 text-white/50 font-mono text-[10px]" dir="ltr">
                    {r.actor_key_prefix || r.created_by || '—'}
                  </td>
                  <td className="px-3 py-2 text-end text-white/50 font-mono text-[10px]" dir="ltr">
                    {String(r.at || r.created_at || '').slice(0, 19).replace('T', ' ')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
